/* eslint-disable max-lines */
import { A } from '@adornis/base/env-info.js';
import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/charts/chartjs/line-chart.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/popover/x-dropdown-selection.js';
import type { XDropdownSelection } from '@adornis/popover/x-dropdown-selection.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { servePdfFromComponent } from '@adornis/print/db/print.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusLevumiItemGerman } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemGerman.js';
import { CampusLevumiLevel } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiLevel.js';
import { LevumiMathCategory, LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import {
  ContentResultEventlog,
  subscribeExerciseResultsForUser,
  type LevumiAnswer,
} from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-accordeon.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import { colors } from '@chancenwerk/design/theme.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { filter, switchMap, tap } from 'rxjs';
import arrow from '../assets/icons/right_arrow.svg';
import { CampusDialogShowLevumiAnswers } from '../components/dialogs/campus-dialog-show-levumi-answers.js';
import { CampusRoutes } from './../routes.js';

enum DiagnosticTabs {
  MATH = 0,
  GERMAN = 1,
  NUMBER_DICTATE = 2,
}

/**
 * @customElement campus-page-student-knowledge-diagnostik-details
 */
@customElement('campus-page-student-knowledge-diagnostik-details')
export class CampusPageStudentKnowledgeDiagnostikDetails extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          height: '100%',
        },
      },
    ];
  }

  @property() selectedUserID = new RXController(this, '');

  @state() size = new WindowSizeController(this);

  @state() private readonly _currentStudent = new RXController(
    this,
    this.selectedUserID.observable.pipe(
      filter(Boolean),
      switchMap(id => ChancenwerkUser.subscribeByID<ChancenwerkUser>(id)({ vorname: 1, nachname: 1, email: 1 })),
    ),
  );

  @state() private readonly _statisticMath = new RXController(
    this,
    this._currentStudent.observable.pipe(
      filter(Boolean),
      switchMap(student =>
        subscribeExerciseResultsForUser(student._id, ExerciseType.LEVUMIMATH)(ContentResultEventlog.allFields),
      ),
    ),
  );

  @state() private readonly _statisticGerman = new RXController(
    this,
    this._currentStudent.observable.pipe(
      filter(Boolean),
      switchMap(student =>
        subscribeExerciseResultsForUser(student._id, ExerciseType.LEVUMIGERMAN)(ContentResultEventlog.allFields),
      ),
    ),
  );

  @state() private readonly _statisticNumberDictate = new RXController(
    this,
    this._currentStudent.observable.pipe(
      filter(Boolean),
      switchMap(student =>
        subscribeExerciseResultsForUser(
          student._id,
          ExerciseType.LEVUMI_NUMBER_DICTATE,
        )(ContentResultEventlog.allFields),
      ),
    ),
  );

  @state() private readonly _selectedLevumiSubjectTab = new RXController(this, DiagnosticTabs.MATH);

  private _openOnce = false;
  @state() private readonly _allLevels = new RXController(
    this,
    this._selectedLevumiSubjectTab.observable.pipe(
      switchMap(tab =>
        tab === DiagnosticTabs.MATH
          ? LearningContentAPI.levumi.subscribeLevelsBySubject(LevumiSubject.MATH)(CampusLevumiLevel.allFields)
          : tab === DiagnosticTabs.GERMAN
          ? LearningContentAPI.levumi.subscribeLevelsBySubject(LevumiSubject.GERMAN)(CampusLevumiLevel.allFields)
          : LearningContentAPI.levumi.subscribeLevelsBySubject(LevumiSubject.NUMBER_DICTATION)(
              CampusLevumiLevel.allFields,
            ),
      ),
      tap(levels => {
        const subject = routingState.value.searchParams.get('subject');
        const level = routingState.value.searchParams.get('level');
        const category = routingState.value.searchParams.get('category');
        const testID = routingState.value.searchParams.get('testID');

        if (!this._openOnce && subject && level) {
          if (this._openFromQuery(subject, level, category ?? '', testID)) {
            this._openOnce = true;
          }
        }
      }),
    ),
  );

  @state() private readonly _selectedLevel = new RXController(this, new CampusLevumiLevel({}));

  @state() private readonly _selectedMathCategory = new RXController(this, LevumiMathCategory.ADD);

  @state() private readonly _selectedResult = new RXController(this, new ContentResultEventlog({}));

  @state() private readonly _numberOfLevumiGermanItems = new RXController(
    this,
    this._selectedLevel.observable.pipe(
      switchMap(level => CampusLevumiItemGerman.numberOfLevumiGermanItemsPerLevel(level._id)()),
    ),
  );

  override render() {
    return permissionGuard(ChancenPermission.campus.READ_DIAGNOSTIC, () => this._content(), {
      forbiddenTemplate: html`<campus-page-no-permission></campus-page-no-permission>`,
      context: ANY_CONTEXT,
    });
  }

  private _content() {
    if (this._currentStudent.isLoading) return this._loader();
    if (!this._currentStudent.value) return this._emptyStudentState();
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex padding="md" space="lg">
          <cr-flex horizontal crossaxis-center>
            <cr-icon ${css({ fontSize: '1.8em', cursor: 'pointer' })} @click=${() => goTo(CampusRoutes.diagnostics)}>
              arrow_back_ios
            </cr-icon>

            <cr-flex space-between horizontal crossaxis-center ${css({ width: '100%' })}>
              <cr-h2> Auswertung der Diagnostik </cr-h2>
              <cr-button
                icon="print"
                mode="outline"
                ?disabled=${this._selectedResult.value.lessonID === ''}
                @click=${async (e: CustomEvent) => {
                  const queryParams = new URLSearchParams({
                    studentID: this._selectedUserID.value ?? '',
                    resultID: this._selectedResult.value._id,
                    type: this._selectedLevumiSubjectTab.value.toString(),
                    levelID: this._selectedLevel.value._id,
                    mathCategory: this._selectedMathCategory.value,
                  }).toString();
                  await openPopupWithLoadingScreen(
                    servePdfFromComponent(
                      'diagnostic-print',
                      queryParams,
                      `diagnostik${encodeURI(this._currentStudent.value?.vorname ?? A.getGloballyUniqueID())}.pdf`,
                    )(),
                  );
                }}
              >
                ${this._selectedResult.value.lessonID !== '' ? 'PDF exportieren' : 'Zuerst Ergebnis auswählen'}
              </cr-button>
            </cr-flex>
          </cr-flex>
          <cr-text> Hier kannst du die Ergbnisse der LEVUMI-Test Durchläufe einsehen. </cr-text>
        </cr-flex>
        <cr-flex space="lg" horizontal> ${this._studentInformationBox()} ${this._chartBox()} </cr-flex>
        ${this._getDataview()}
      </cr-flex>
    `;
  }

  private _loader() {
    return html`
      <cr-flex center crossaxis-center w100 h100>
        <cr-campus-loader></cr-campus-loader>
      </cr-flex>
    `;
  }

  private _emptyStudentState() {
    return html`
      <cr-flex center crossaxis-center w100 h100 space="sm">
        <cr-h4>Oops, diese Person wurde nicht gefunden.</cr-h4>
        <cr-button @click=${() => goTo(CampusRoutes.diagnostics)}>ZURÜCK</cr-button>
      </cr-flex>
    `;
  }

  private _emptyState() {
    return html`
      <cr-flex center crossaxis-center space="sm">
        <cr-text>Hierzu gibt es keine Daten oder das Level muss gewählt werden.</cr-text>
      </cr-flex>
    `;
  }

  private _openFromQuery(subject: string, level: string, category: string, testID: Maybe<string>) {
    this._selectedLevumiSubjectTab.next(
      { [LevumiSubject.MATH]: 0, [LevumiSubject.GERMAN]: 1, [LevumiSubject.NUMBER_DICTATION]: 2 }[subject] ?? 0,
    );

    const levelFound = this._allLevels.value?.find(l => l.level === level && l.subject === subject);
    if (!levelFound) {
      return false;
    }

    this._selectedLevel.next(levelFound);
    if (category.length > 0) {
      this._selectedMathCategory.next(category as LevumiMathCategory);
    }

    if (testID && testID.length > 0) {
      const testResult = this._getResultsForTab(this._selectedLevumiSubjectTab.value)?.find(r => r._id === testID);
      if (testResult) {
        this._selectedResult.next(testResult);
        if (this.testDropdownRef.value) {
          this.testDropdownRef.value.value.next(testResult);
        } else {
          return false;
        }
      } else {
        return false;
      }
      return true;
    }

    const testResult = this._getResultsForTab(this._selectedLevumiSubjectTab.value)?.at(-1);
    if (testResult) {
      this._selectedResult.next(testResult);
      if (this.testDropdownRef.value) {
        this.testDropdownRef.value.value.next(testResult);
      } else {
        return false;
      }
    }

    return true;
  }

  private _studentInformationBox() {
    return html`
      <cr-card space="lg" ${css({ width: '50%' })}>
        <cr-flex space="lg">
          <cr-h4> ${this._currentStudent.value?.vorname} ${this._currentStudent.value?.nachname} </cr-h4>
          <cr-flex space="md">
            <cr-flex space="sm" ${css({ flex: '1 1 auto' })}>
              <cr-dropdown-selection
                ${css({ width: '100%' })}
                placeholder="Fach"
                .value=${this._selectedLevumiSubjectTab.value}
                @value-picked=${(e: ValueEvent<number>) => {
                  this._selectedLevumiSubjectTab.next(e.detail.value);
                  this._selectedLevel.next(new CampusLevumiLevel({}));
                }}
                .selectables=${[0, 1, 2]}
                .renderString=${item => {
                  switch (item) {
                    case 0:
                      return 'Mathe';
                    case 1:
                      return 'Deutsch';
                    case 2:
                      return 'Zahlendiktat';

                    default:
                      break;
                  }
                }}
              >
              </cr-dropdown-selection>
            </cr-flex>
            <cr-flex space="sm" ${css({ flex: '1 1 auto' })}>
              <cr-text> Levumi-Level </cr-text>
              <cr-flex space="md" horizontal>
                ${this._selectedLevumiSubjectTab.value === 0
                  ? html`
                      <cr-dropdown-selection
                        ${css({ width: '100%' })}
                        placeholder="Kategorie"
                        .value=${this._selectedMathCategory.value}
                        @value-picked=${(e: ValueEvent<LevumiMathCategory>) => {
                          this._selectedMathCategory.next(e.detail.value);
                        }}
                        .selectables=${Object.values(LevumiMathCategory)}
                      >
                      </cr-dropdown-selection>
                    `
                  : nothing}
                <cr-dropdown-selection
                  ${css({ width: '100%' })}
                  placeholder="Level"
                  .value=${this._selectedLevel.value}
                  @value-picked=${(e: ValueEvent<CampusLevumiLevel>) => {
                    this._selectedLevel.next(e.detail.value);
                    this._selectedResult.next(new ContentResultEventlog({}));
                    if (this.testDropdownRef.value) {
                      this.testDropdownRef.value.value.next(null);
                    }
                  }}
                  .selectables=${this._filterStations(
                    this._allLevels.value?.filter(
                      level =>
                        level.subject ===
                        (this._selectedLevumiSubjectTab.value === 0
                          ? LevumiSubject.MATH
                          : this._selectedLevumiSubjectTab.value === 1
                          ? LevumiSubject.GERMAN
                          : LevumiSubject.NUMBER_DICTATION),
                    ) ?? [],
                  ).sort((a, b) => (a.level.trim().toUpperCase() < b.level.trim().toUpperCase() ? -1 : 1))}
                  .renderString=${e => e.level}
                >
                </cr-dropdown-selection>
              </cr-flex>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }
  private _generateDataForMathChart(level: CampusLevumiLevel): any {
    const statsForLevel = this._statisticMath.value?.filter(
      stat =>
        stat.levumiResultInformation.levumiLevel?._id === level._id &&
        stat.levumiResultInformation.levumiMathCategory === this._selectedMathCategory.value,
    );

    const data = {
      labels: (statsForLevel ?? []).map(s =>
        DateTime.fromISO(s.createdAt.toISOString()).toLocaleString(DateTime.DATETIME_SHORT),
      ),
      datasets: [
        {
          label: 'Richtige Antworten',
          data: statsForLevel?.map(s => s.levumiResultInformation.correctlyAnswered ?? 0),
          borderColor: '#E28807',
        },
      ],
    };
    return data;
  }

  private _generateDataForNumberDictateChart(level: CampusLevumiLevel): any {
    const statsForLevel = this._statisticNumberDictate.value?.filter(
      stat => stat.levumiResultInformation.levumiLevel?._id === level._id,
    );

    const data = {
      labels: (statsForLevel ?? []).map(s =>
        DateTime.fromISO(s.createdAt.toISOString()).toLocaleString(DateTime.DATETIME_SHORT),
      ),
      datasets: [
        {
          label: 'Richtige Antworten',
          data: statsForLevel?.map(s => s.levumiResultInformation.correctlyAnswered ?? 0),
          borderColor: '#E28807',
        },
      ],
    };
    return data;
  }

  private _generateDataForGermanChart(level: CampusLevumiLevel): any {
    const statsForLevel = this._statisticGerman.value?.filter(
      stat => stat.levumiResultInformation.levumiLevel?._id === level._id,
    );
    const data = {
      labels: (statsForLevel ?? []).map(s =>
        DateTime.fromISO(s.createdAt.toISOString()).toLocaleString(DateTime.DATETIME_FULL),
      ),

      datasets: [
        {
          label: 'Richtige Antworten',
          data: statsForLevel?.map(s => s.levumiResultInformation.correctlyAnswered ?? 0),
          borderColor: '#E28807',
        },
      ],
    };
    return data;
  }

  private _chartBox() {
    const statsForLevel =
      this._selectedLevumiSubjectTab.value === 0
        ? this._statisticMath.value?.filter(
            stat => stat.levumiResultInformation.levumiLevel?._id === this._selectedLevel.value._id,
          )
        : this._selectedLevumiSubjectTab.value === 1
        ? this._statisticGerman.value?.filter(
            stat => stat.levumiResultInformation.levumiLevel?._id === this._selectedLevel.value._id,
          )
        : this._statisticNumberDictate.value?.filter(
            stat => stat.levumiResultInformation.levumiLevel?._id === this._selectedLevel.value._id,
          );
    if (!statsForLevel) return nothing;

    const data =
      this._selectedLevumiSubjectTab.value === 0
        ? this._generateDataForMathChart(this._selectedLevel.value)
        : this._selectedLevumiSubjectTab.value === 1
        ? this._generateDataForGermanChart(this._selectedLevel.value)
        : this._generateDataForNumberDictateChart(this._selectedLevel.value);
    if (!data?.datasets[0]?.data?.length || !statsForLevel.length)
      return html`
        <cr-card space="lg" ${css({ width: '100%' })} crossaxis-center center> ${this._emptyState()} </cr-card>
      `;

    return html`
      <cr-card space="lg" ${css({ width: '100%' })}>
        <cr-flex space="lg">
          <line-chart
            ${css({ width: '100%' })}
            .data=${data}
            .options=${{
              responsive: true,

              scales: {
                y: {
                  beginAtZero: true,
                  max: this._selectedLevumiSubjectTab.value === 1 ? this._numberOfLevumiGermanItems.value ?? 0 : 100,
                },
              },
              showTooltips: true,
              plugins: {
                tooltip: {
                  enabled: true,
                },
              },
            }}
          ></line-chart>
        </cr-flex>
      </cr-card>
    `;
  }

  private _getResultsForTab(tabValue: DiagnosticTabs) {
    let results =
      (tabValue === DiagnosticTabs.MATH
        ? this._statisticMath.value
        : tabValue === DiagnosticTabs.GERMAN
        ? this._statisticGerman.value
        : this._statisticNumberDictate.value) ?? [];
    if (tabValue === DiagnosticTabs.MATH) {
      results = results.filter(
        r =>
          r.levumiResultInformation.levumiLevel?._id === this._selectedLevel.value._id &&
          r.levumiResultInformation.levumiMathCategory === this._selectedMathCategory.value,
      );
    } else if (tabValue === DiagnosticTabs.GERMAN) {
      results = results.filter(r => r.levumiResultInformation.levumiLevel?._id === this._selectedLevel.value._id);
    } else if (tabValue === DiagnosticTabs.NUMBER_DICTATE) {
      results = results.filter(r => r.levumiResultInformation.levumiLevel?._id === this._selectedLevel.value._id);
    }

    return results;
  }

  private _getUsedDimensionsForCurrentTest() {
    const usedDimensionsMap = new Map<string, LevumiAnswer[]>();
    if (this._selectedLevumiSubjectTab.value === DiagnosticTabs.MATH) {
      (this._selectedResult.value.levumiResultInformation.questionsAndAnswers ?? []).forEach(qa => {
        if (qa.difficulty) {
          if (!usedDimensionsMap.has(qa.difficulty.toString())) usedDimensionsMap.set(qa.difficulty.toString(), [qa]);
          else usedDimensionsMap.get(qa.difficulty.toString())?.push(qa);
        }
      });
    } else if (this._selectedLevumiSubjectTab.value === DiagnosticTabs.GERMAN) {
      (this._selectedResult.value.levumiResultInformation.questionsAndAnswers ?? []).forEach(qa => {
        if (qa.category) {
          if (!usedDimensionsMap.has(qa.category)) usedDimensionsMap.set(qa.category, [qa]);
          else usedDimensionsMap.get(qa.category)?.push(qa);
        }
      });
    } else {
      (this._selectedResult.value.levumiResultInformation.questionsAndAnswers ?? []).forEach(qa => {
        if (qa.difficulty) {
          if (!usedDimensionsMap.has(qa.difficulty.toString())) usedDimensionsMap.set(qa.difficulty.toString(), [qa]);
          else usedDimensionsMap.get(qa.difficulty.toString())?.push(qa);
        }
      });
    }

    return usedDimensionsMap;
  }

  testDropdownRef: Ref<XDropdownSelection<ContentResultEventlog>> = createRef();

  private _getDataview() {
    const testResults = this._getResultsForTab(this._selectedLevumiSubjectTab.value);

    if (!testResults.length || !this._selectedLevel.value.level)
      return html`<cr-card space="lg" ${css({ width: '100%' })}>${this._emptyState()}</cr-card>`;

    const levumiResultForTest = this._selectedResult.value.levumiResultInformation;

    return html`
      <cr-card space="lg" ${css({ width: '100%' })}>
        <cr-flex space="lg">
          <cr-flex space-between horizontal crossaxis-center>
            <cr-grid
              columns="1fr max-content"
              horizontal
              crossaxis-center
              space="md"
              ${css({ flexBasis: '40%', alignItems: 'center' })}
            >
              <cr-dropdown-selection
                ${ref(this.testDropdownRef)}
                placeholder="Tests"
                .selectables=${testResults}
                .renderString=${(item: ContentResultEventlog) =>
                  DateTime.fromJSDate(item.createdAt).toFormat('dd LLL yyyy HH:mm')}
                @value-picked=${(e: ValueEvent<ContentResultEventlog>) => {
                  this._selectedResult.next(e.detail.value);
                }}
              ></cr-dropdown-selection>
              ${permissionGuard(ChancenPermission.campus.DELETE_LEVUMI_TESTS, () => {
                if (!this._selectedResult.value._id) {
                  return nothing;
                }
                return html`<cr-button
                  mode="icon"
                  tone="error"
                  icon="delete"
                  @click=${async () => {
                    await ChromiumDialog.guard(
                      async () => {
                        await this._selectedResult.value.remove();
                        this._selectedResult.next(new ContentResultEventlog({}));
                        if (this.testDropdownRef.value) {
                          this.testDropdownRef.value.value.next(null);
                        }
                        this.requestUpdate();
                      },
                      {
                        hasToConfirm: true,
                        confirmText: 'Willst du dieses Test-Ergebnis wirklich löschen?',
                      },
                    );
                  }}
                ></cr-button>`;
              })}
            </cr-grid>
            <cr-flex ${css({ flexBasis: '40%' })} crossaxis-center space="sm">
              ${this._selectedResult.value._id
                ? html`
                    <cr-progress-bar
                      ${css({ width: '100%' })}
                      .value=${levumiResultForTest.correctlyAnsweredPercent}
                    ></cr-progress-bar>
                    <cr-text>
                      ${((levumiResultForTest.correctlyAnsweredPercent ?? 0) * 100).toFixed(2)}% |
                      ${levumiResultForTest.correctlyAnswered}/${levumiResultForTest.overallAnswered}
                    </cr-text>
                  `
                : nothing}
            </cr-flex>
          </cr-flex>
          <cr-flex>
            ${this._selectedResult.value._id
              ? this._getResultGrid()
              : html`
                  <cr-flex center crossaxis-center>
                    <cr-text>Es muss ein Test gewählt werden</cr-text>
                  </cr-flex>
                `}
          </cr-flex>
        </cr-flex>
      </cr-card>
    `;
  }

  private _getResultGrid() {
    const usedDimensionsInTestMap = this._getUsedDimensionsForCurrentTest();

    // Sort after difficulty in Math diagnostic
    const usedDimensions =
      this._selectedLevumiSubjectTab.value === DiagnosticTabs.MATH
        ? Array.from(usedDimensionsInTestMap.keys()).sort(function (a, b) {
            return parseInt(a) - parseInt(b);
          })
        : usedDimensionsInTestMap.keys();

    const correctAnswers = (this._selectedResult.value.levumiResultInformation.questionsAndAnswers ?? []).filter(
      a => a.answerCorrect,
    );
    const wrongAnswers = (this._selectedResult.value.levumiResultInformation.questionsAndAnswers ?? []).filter(
      a => !a.answerCorrect,
    );

    return html`
      <cr-grid columns="5" center>
        <cr-flex padding="sm">
          <cr-text> ${this._selectedLevumiSubjectTab.value === 0 ? 'Schwierigkeitsgrad' : 'Dimension'} </cr-text>
        </cr-flex>
        <cr-flex padding="sm"> <cr-text>Richtig</cr-text> </cr-flex>
        <cr-flex padding="sm"> <cr-text>Falsch</cr-text> </cr-flex>
        <cr-flex padding="sm"> <cr-text>Genauigkeit</cr-text> </cr-flex>
        <cr-flex padding="sm"> <cr-text>Geschwindigkeit</cr-text> </cr-flex>
        ${repeat(
          usedDimensions,
          dim => dim,
          dim => {
            const correctAnswersInDim = usedDimensionsInTestMap.get(dim)?.filter(a => a.answerCorrect);
            const wrongAnswersInDim = usedDimensionsInTestMap.get(dim)?.filter(a => !a.answerCorrect);
            return html`
              <cr-flex padding="sm"> <cr-text>${dim}</cr-text> </cr-flex>
              <cr-flex padding="sm" horizontal space="sm">
                <cr-text> ${correctAnswersInDim?.length} </cr-text>

                ${correctAnswersInDim?.length ? this._getLevumiAnswerDialogIcon(correctAnswersInDim, []) : nothing}
              </cr-flex>
              <cr-flex padding="sm" horizontal space="sm">
                <cr-text> ${wrongAnswersInDim?.length} </cr-text>

                ${wrongAnswersInDim?.length ? this._getLevumiAnswerDialogIcon([], wrongAnswersInDim) : nothing}
              </cr-flex>
              <cr-flex padding="sm"> <cr-text>-</cr-text> </cr-flex>
              <cr-flex padding="sm"> <cr-text>-</cr-text> </cr-flex>
            `;
          },
        )}
        <cr-flex
          ${css({ background: `${colors.chancen_green_primary}32` })}
          horizontal
          space="sm"
          crossaxis-center
          padding="sm"
        >
          <cr-text>Gesamt</cr-text>
          ${this._selectedResult.value.lessonID !== ''
            ? this._getLevumiAnswerDialogIcon(correctAnswers, wrongAnswers)
            : nothing}
        </cr-flex>
        <cr-flex ${css({ background: `${colors.chancen_green_primary}32` })} padding="sm" horizontal space="sm">
          <cr-text> ${correctAnswers.length} </cr-text>

          ${correctAnswers.length ? this._getLevumiAnswerDialogIcon(correctAnswers, []) : nothing}
        </cr-flex>
        <cr-flex ${css({ background: `${colors.chancen_green_primary}32` })} padding="sm" horizontal space="sm">
          <cr-text> ${wrongAnswers.length} </cr-text>

          ${wrongAnswers.length ? this._getLevumiAnswerDialogIcon([], wrongAnswers) : nothing}
        </cr-flex>
        <cr-flex ${css({ background: `${colors.chancen_green_primary}32` })} padding="sm">
          <cr-text>
            ${this._selectedResult.value.levumiResultInformation.readingAccuracyInPercent?.toFixed(2)}
          </cr-text>
        </cr-flex>
        <cr-flex ${css({ background: `${colors.chancen_green_primary}32` })} padding="sm">
          <cr-text> ${this._selectedResult.value.levumiResultInformation.readingSpeedInQuePerMin?.toFixed(2)} </cr-text>
        </cr-flex>
      </cr-grid>
    `;
  }

  private _getLevumiAnswerDialogIcon(correctAnswers: LevumiAnswer[], wrongAnswers: LevumiAnswer[]) {
    return html`
      <cr-icon
        style="cursor: pointer;"
        type="img"
        src=${arrow}
        @click=${async () => {
          await CampusDialogShowLevumiAnswers.showPopup({
            draggable: this.size.mobile || this.size.tablet,
            props: {
              subject: this._selectedLevumiSubjectTab.value === 1 ? LevumiSubject.GERMAN : LevumiSubject.MATH,
              correctAnswers,
              wrongAnswers,
            },
          });
        }}
      >
      </cr-icon>
    `;
  }

  private _filterStations(levels: CampusLevumiLevel[]) {
    return levels.filter(l => {
      if (this._selectedMathCategory.value !== LevumiMathCategory.MUL && l.level === 'Station 6') return false;
      if (this._selectedMathCategory.value !== LevumiMathCategory.MUL && l.level === 'Station 5') return false;
      return true;
    });
  }
}
